import { PrescriptionItem } from "./presriptionData";

export interface PrescriptionData {
  prescriber: {
    name: string;
    email: string;
    authCode: string;
  };
  patient: {
    name: string;
    dateOfBirth: string;
    phone: string;
    allergies: string;
  };
  clinic: {
    name: string;
    address: string;
    email: string;
    phone: string;
  };
  items: PrescriptionItem[];
  timestamp: Date;
}

// Default prescription data
export const getDefaultPrescriptionData = (): PrescriptionData => ({
  prescriber: {
    name: "Dr. Andrew Staton",
    email: "andrew.staton@signatureclinic.co.uk",
    authCode: "303346",
  },
  patient: {
    name: "Patient Name",
    dateOfBirth: "DD/MM/YYYY",
    phone: "Unknown",
    allergies: "No known drug allergies",
  },
  clinic: {
    name: "Signature Health Clinic",
    address: "123 Wellness Street, London, UK",
    email: "info@signatureclinic.co.uk",
    phone: "+44 1234 567890",
  },
  items: [],
  timestamp: new Date(),
});

export const generatePrescriptionHTML = (data: PrescriptionData): string => {
  const formatDate = (date: Date | string): string => {
    if (!date) return "No date";

    const pad = (value: number | string) => String(value).padStart(2, "0");
    const trimmedValue = typeof date === "string" ? date.trim() : "";

    if (typeof date === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}/.test(trimmedValue)) {
      const [day, month, year] = trimmedValue.split(/[\/\s,]/).filter(Boolean);
      return `${pad(day)}/${pad(month)}/${year}`;
    }

    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(trimmedValue)) {
      const [year, month, day] = trimmedValue.split("-");
      return `${pad(day)}/${pad(month)}/${year}`;
    }

    const parsedDate = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
      return "Invalid date";
    }

    return `${pad(parsedDate.getDate())}/${pad(parsedDate.getMonth() + 1)}/${parsedDate.getFullYear()}`;
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const medicationRows = data.items
    .filter((item) => item.medication && item.dosage)
    .map(
      (item) => `
      <tr>
        <td>
        <div class="medication-name">
          ${[item.medication, item.strength, item.medicationType]
            .filter(Boolean)
            .join(" ")}
        </div>
          ${item.repeatPrescription ? '<div class="dosage-info">🔄 Repeat Prescription</div>' : ""}
        </td>
        <td>${item.quantity || "As prescribed"}</td>
        <td>
          <div>${item.dosage}</div>
          ${item.medicationDispensed ? '<div class="dosage-info">✅ Dispensed</div>' : ""}
        </td>
      </tr>
    `,
    )
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Private e-Prescription</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  :root {
    --primary-blue: #1e40af;
    --primary-blue-light: #3b82f6;
    --accent-teal: #0d9488;
    --text-dark: #1f2937;
    --text-medium: #4b5563;
    --text-light: #6b7280;
    --border-light: #e5e7eb;
    --bg-primary: #ffffff;
    --bg-secondary: #f8fafc;
    --bg-accent: #f0f9ff;
    --shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    margin: 0;
    padding: 15px 10px;
    line-height: 1.4;
    color: var(--text-dark);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-size: 14px;
  }

  .prescription-container {
    max-width: 210mm;
    max-height: 297mm;
    margin: 0 auto;
    background: var(--bg-primary);
    border-radius: 12px;
    box-shadow: var(--shadow);
    overflow: hidden;
    position: relative;
    border: 1px solid var(--border-light);
    page-break-inside: avoid;
  }

  .prescription-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-blue) 0%, var(--accent-teal) 100%);
    z-index: 1;
  }

  .watermark {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 120px;
    font-weight: 100;
    color: rgba(30, 64, 175, 0.03);
    z-index: 0;
    pointer-events: none;
    user-select: none;
    font-family: 'Inter', sans-serif;
    letter-spacing: 8px;
  }

  .clinic-header {
    display: flex;
    align-items: center;
    background: var(--bg-secondary);
    padding: 20px 25px;
    border-bottom: 2px solid var(--border-light);
    position: relative;
    z-index: 2;
  }

  .clinic-logo {
    width: 60px;
    height: 60px;
    margin-right: 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(30, 64, 175, 0.15);
    background: var(--bg-primary);
    padding: 8px;
  }

  .clinic-info {
    flex-grow: 1;
  }

  .clinic-info h2 {
    font-size: 20px;
    font-weight: 700;
    color: var(--primary-blue);
    margin-bottom: 4px;
    letter-spacing: -0.5px;
  }

  .clinic-info .address {
    font-size: 13px;
    color: var(--text-medium);
    margin-bottom: 2px;
    font-weight: 400;
  }

  .clinic-info .contact {
    font-size: 12px;
    color: var(--text-light);
    font-weight: 400;
  }

  .prescription-number {
    text-align: right;
    color: var(--text-medium);
    font-size: 12px;
  }

  .prescription-number .number {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    color: var(--primary-blue);
    font-size: 14px;
  }

  .header {
    background: linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-blue-light) 100%);
    color: white;
    padding: 16px 25px;
    position: relative;
    z-index: 2;
  }

  .header h1 {
    font-size: 22px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    text-align: center;
    margin: 0;
  }

  .section {
    padding: 18px 25px;
    border-bottom: 1px solid var(--border-light);
    position: relative;
    z-index: 2;
  }

  .section:last-child {
    border-bottom: none;
  }

  .section-title {
    font-size: 15px;
    color: var(--primary-blue);
    font-weight: 600;
    margin-bottom: 12px;
    padding-bottom: 6px;
    border-bottom: 2px solid var(--bg-accent);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .section-title::before {
    content: '';
    width: 3px;
    height: 18px;
    background: linear-gradient(180deg, var(--primary-blue) 0%, var(--accent-teal) 100%);
    border-radius: 2px;
  }

  .details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin-top: 2px;
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .detail-label {
    font-size: 11px;
    color: var(--text-light);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .detail-value {
    font-size: 14px;
    color: var(--text-dark);
    font-weight: 500;
    padding: 4px 0;
  }

  .prescription-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    margin-top: 12px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
  }

  .prescription-table th {
    background: linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-blue-light) 100%);
    color: white;
    padding: 10px 12px;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border: none;
  }

  .prescription-table th:first-child {
    border-top-left-radius: 8px;
  }

  .prescription-table th:last-child {
    border-top-right-radius: 8px;
  }

  .prescription-table td {
    padding: 12px;
    font-size: 13px;
    background: var(--bg-primary);
    border-bottom: 1px solid var(--border-light);
    vertical-align: top;
  }

  .prescription-table tbody tr:last-child td {
    border-bottom: none;
  }

  .prescription-table tbody tr:nth-child(even) td {
    background: var(--bg-secondary);
  }

  .medication-name {
    font-weight: 600;
    color: var(--text-dark);
  }

  .dosage-info {
    color: var(--text-medium);
    font-size: 11px;
    margin-top: 2px;
  }

  .digital-signature {
    background: var(--bg-accent);
    padding: 16px 25px;
    text-align: center;
    border-top: 2px solid var(--primary-blue-light);
    position: relative;
    z-index: 2;
  }

  .signature-content {
    max-width: 500px;
    margin: 0 auto;
  }

  .signature-icon {
    width: 32px;
    height: 32px;
    background: var(--primary-blue);
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
    color: white;
    font-size: 16px;
  }

  .signature-text {
    font-size: 13px;
    color: var(--text-medium);
    margin-bottom: 8px;
    line-height: 1.5;
  }

  .signature-details {
    font-size: 12px;
    color: var(--text-light);
    font-weight: 500;
  }

  .auth-code {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    color: var(--primary-blue);
    background: var(--bg-primary);
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid var(--border-light);
  }

  .footer {
    background: var(--text-dark);
    color: white;
    padding: 12px 25px;
    text-align: center;
    font-size: 10px;
    line-height: 1.4;
    position: relative;
    z-index: 2;
  }

  .footer-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 500px;
    margin: 0 auto;
    flex-wrap: wrap;
    gap: 12px;
  }

  .page-info {
    color: #9ca3af;
  }

  .compliance-info {
    color: #d1d5db;
    font-weight: 500;
    font-size: 9px;
  }

  @page {
    size: A4;
    margin: 10mm;
  }

  @media print {
    body {
      background: white !important;
      padding: 0 !important;
      margin: 0 !important;
      font-size: 12px !important;
    }

    .prescription-container {
      box-shadow: none !important;
      border: 1px solid #000 !important;
      border-radius: 0 !important;
      max-width: 100% !important;
      max-height: 100% !important;
      page-break-inside: avoid !important;
      display: block !important;
    }

    .watermark {
      display: none !important;
    }

    .clinic-header,
    .header,
    .section,
    .digital-signature,
    .footer {
      page-break-inside: avoid !important;
    }

    .prescription-table {
      page-break-inside: avoid !important;
    }
  }

  @media (max-width: 768px) {
    body {
      padding: 15px;
    }

    .clinic-header,
    .header,
    .section,
    .digital-signature,
    .footer {
      padding-left: 20px;
      padding-right: 20px;
    }

    .details-grid {
      grid-template-columns: 1fr;
    }

    .footer-content {
      flex-direction: column;
      text-align: center;
    }
  }
</style>
</head>
<body>
  <div class="prescription-container">
    <div class="watermark">PRESCRIPTION</div>

    <div class="clinic-header">
      <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a7/Health_Medical_Cross_Icon.svg/1024px-Health_Medical_Cross_Icon.svg.png" alt="Clinic Logo" class="clinic-logo">
      <div class="clinic-info">
        <h2>${data.clinic.name}</h2>
        <div class="address">${data.clinic.address}</div>
        <div class="contact">📧 ${data.clinic.email} • 📞 ${data.clinic.phone}</div>
      </div>
      <div class="prescription-number">
        <div>Prescription No.</div>
        <div class="number">RX-${data.prescriber.authCode}</div>
      </div>
    </div>

    <div class="header">
      <h1>Private e-Prescription</h1>
    </div>

    <div class="section">
      <div class="section-title">👨‍⚕️ Prescriber Information</div>
      <div class="details-grid">
        <div class="detail-item">
          <div class="detail-label">Prescriber Name</div>
          <div class="detail-value">${data.prescriber.name}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Email Address</div>
          <div class="detail-value">${data.prescriber.email}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Authorisation Code</div>
          <div class="detail-value auth-code">${data.prescriber.authCode}</div>
        </div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">👤 Patient Information</div>
      <div class="details-grid">
        <div class="detail-item">
          <div class="detail-label">Patient Name</div>
          <div class="detail-value">${data.patient.name}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Date of Birth</div>
          <div class="detail-value">${data.patient.dateOfBirth}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Phone Number</div>
          <div class="detail-value">${data.patient.phone}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Known Allergies</div>
          <div class="detail-value">${data.patient.allergies}</div>
        </div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">💊 Prescribed Medications</div>
      <table class="prescription-table">
        <thead>
          <tr>
            <th>Medication & Strength</th>
            <th>Quantity</th>
            <th>Dosage Instructions</th>
          </tr>
        </thead>
        <tbody>
          ${medicationRows}
        </tbody>
      </table>
    </div>

    <div class="digital-signature">
      <div class="signature-content">
        <div class="signature-icon">✓</div>
        <div class="signature-text">
          This prescription was digitally signed on <strong>${formatDate(data.timestamp)} at ${formatTime(data.timestamp)}</strong>
          and is valid without a handwritten signature.
        </div>
        <div class="signature-details">
          Digital Authorisation Code: <span class="auth-code">${data.prescriber.authCode}</span>
        </div>
      </div>
    </div>

    <div class="footer">
      <div class="footer-content">
        <div class="page-info">Page 1 of 1</div>
        <div class="compliance-info">
          This prescription is valid for private dispensing only. Ensure compliance with all applicable regulations.
        </div>
      </div>
    </div>
  </div>
</body>
</html>`;
};

export const generatePrescriptionPDF = async (
  data: PrescriptionData,
): Promise<string> => {
  const html = generatePrescriptionHTML(data);

  // Create a blob with the HTML content
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);

  // Open in new window for printing/saving as PDF
  const printWindow = window.open(url, "_blank");
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.print();
      URL.revokeObjectURL(url);
    };
  }

  return url;
};

export const downloadPrescriptionHTML = (data: PrescriptionData): void => {
  const html = generatePrescriptionHTML(data);
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `prescription-${data.patient.name.replace(/\s+/g, "-")}-${data.timestamp.toISOString().split("T")[0]}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

// Preview prescription in a modal/window
export const previewPrescription = (data: PrescriptionData): void => {
  const html = generatePrescriptionHTML(data);
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);

  const previewWindow = window.open(
    url,
    "_blank",
    "width=900,height=700,scrollbars=yes",
  );
  if (previewWindow) {
    previewWindow.onbeforeunload = () => {
      URL.revokeObjectURL(url);
    };
  }
};
